/**
 * TableDetail — Full table account management view.
 * Orchestrates: useTableAccount, useTableActions, TableAccountContent, TableDialogs.
 */
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Receipt, FileText, ArrowRightLeft, XCircle, Trash2, Printer, DoorOpen } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";
import { useTableAccount } from "@/hooks/useTableAccount";
import { useTableActions } from "@/hooks/useTableActions";
import { TableAccountContent } from "@/components/tables/TableAccountContent";
import { TableDialogs } from "@/components/tables/TableDialogs";
import { TableDetailQrSection } from "@/components/tables/TableDetailQrSection";

const BACKEND_URL = API_URL;

function TableDetail({ table, onBack, onRefresh, restaurantSlug, customDomain, restaurant }) {
  const { authAxios, user } = useAuth();
  const { t } = useTranslation();

  const { account, loading, fetchAccount, allTables, restaurantData } = useTableAccount(authAxios, table, restaurant);

  const {
    showSplit, setShowSplit,
    showTransferItem, setShowTransferItem,
    showTransferAll, setShowTransferAll,
    showCancel, setShowCancel,
    showPayment, setShowPayment,
    showDiscount, setShowDiscount,
    splitMode, setSplitMode, splitPersons, setSplitPersons, splitResult, setSplitResult, selectedSplitItems,
    selectedTransferItems, setSelectedTransferItems, targetTableId, setTargetTableId,
    paymentMethod, setPaymentMethod, cardType, setCardType, cashReceived, setCashReceived, needsChange, setNeedsChange,
    paymentAmount, setPaymentAmount, processingPayment,
    paymentMode, setPaymentMode, partialPayments, currentPartialAmount, setCurrentPartialAmount,
    discountType, setDiscountType, discountValue, setDiscountValue, appliedDiscount, setAppliedDiscount,
    processingInvoice,
    finalTotal, totalPartialPaid, remainingToPay, changeAmount, partialCashChange,
    handleCancel, handleSplitCalc, handleTransferItems, handleTransferAll,
    toggleSplitItem, toggleTransferItem, handleAddPartialPayment, handleRemovePartialPayment,
    handlePayment, resetPaymentState, handleApplyDiscount, handleEmitInvoice, handleCloseTable,
    lastPaymentResult, setLastPaymentResult, handlePrintReceipt,
  } = useTableActions({ authAxios, table, account, fetchAccount, onRefresh, onBack });

  const tableName = table.name || `MESA ${String(table.number).padStart(2, "0")}`;
  const base = restaurantSlug || user?.restaurant_id;
  const menuUrl = customDomain
    ? `https://${customDomain}/m/${base}?table=${table.id}`
    : `${BACKEND_URL}/m/${base}?table=${table.id}`;

  const otherTables = allTables;
  const orders = account?.orders || [];
  const payments = account?.payments || [];
  const total = account?.total || 0;
  const pending = account?.pending || 0;
  const tableIsOccupied = (account?.table?.status ?? table?.status) !== "available";

  // Payments that belong to the CURRENT session (after occupied_since).
  // Fallback: if occupied_since is null (race condition or old DB record),
  // use the earliest active order's created_at as session boundary so that
  // payments from previous sessions are never mixed into the current one.
  const occupiedSince = account?.table?.occupied_since;
  const earliestOrderDate = orders.length > 0
    ? orders.reduce((min, o) => (!min || (o.created_at && o.created_at < min)) ? o.created_at : min, null)
    : null;
  const sessionBoundary = occupiedSince || earliestOrderDate;
  const sessionPayments = sessionBoundary
    ? payments.filter(p => p.date && p.date >= sessionBoundary)
    : payments;
  // The backend total already excludes paid orders. Never subtract them twice.
  const sessionPending = pending;
  const allItems = orders.flatMap((order) =>
    order.items?.map((item, ii) => ({ ...item, order_id: order.id, item_index: ii, order_date: order.created_at })) || []
  );

  const deleteTable = async () => {
    try { await authAxios.delete(`/tables/${table.id}`); toast.success(t("tables.tableCleared")); await onRefresh(); onBack(); }
    catch { toast.error(t("tables.errorDeletingTable")); }
  };

  const forceResetTable = async () => {
    try {
      await authAxios.post(`/tables/${table.id}/force-reset`);
      toast.success(t("tables.tableCleared"));
      onRefresh();
    } catch { toast.error(t("tables.errorClearingTable")); }
  };

  return (
    <div data-testid="table-detail">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-4">
        <button className="text-emerald-700 hover:underline" onClick={onBack}>Inicio</button>
        <span className="text-gray-400">/</span>
        <button className="text-emerald-700 hover:underline" onClick={onBack}>Mesas</button>
        <span className="text-gray-400">/</span>
        <span className="font-medium text-gray-700">{tableName}</span>
      </div>

      {/* Main Action Buttons */}
      <div className="flex items-center gap-3 mb-4">
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm gap-2 px-6 py-3 h-auto" onClick={() => { setShowPayment(true); setPaymentAmount(finalTotal.toString()); }} disabled={sessionPending <= 0} data-testid="pay-table-btn">
          <Receipt className="w-5 h-5" /> PAGAR
        </Button>
        <Button variant="outline" className="border-amber-400 text-amber-700 font-bold uppercase text-sm gap-2 px-6 py-3 h-auto" onClick={() => { setShowDiscount(true); setDiscountValue(""); }} disabled={total <= 0} data-testid="discount-btn">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z" /></svg>
          DESCONTO
        </Button>
        <Button variant="outline" className="border-purple-400 text-purple-700 font-bold uppercase text-sm gap-2 px-6 py-3 h-auto" onClick={handleEmitInvoice} disabled={orders.length === 0 || processingInvoice} data-testid="emit-invoice-btn">
          <FileText className="w-5 h-5" />
          {processingInvoice ? "EMITINDO..." : "NFC-e / VENDUS"}
        </Button>
      </div>

      {/* Post-payment: print receipt + encerrar mesa */}
      {lastPaymentResult && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-800">
                Pagamento registado
                {lastPaymentResult.orders_released > 0 && ` • ${lastPaymentResult.orders_released} pedido(s) liberado(s) para cozinha`}
              </p>
            </div>
            <Button size="sm" variant="outline" className="border-emerald-400 text-emerald-700 gap-1.5 font-bold uppercase text-xs" onClick={handlePrintReceipt} data-testid="print-receipt-btn">
              <Printer className="w-3.5 h-3.5" /> IMPRIMIR RECIBO
            </Button>
            <button className="text-gray-400 hover:text-gray-600 ml-1" onClick={() => setLastPaymentResult(null)} aria-label="Fechar">✕</button>
          </div>
          {/* Always show close button after payment — staff decides when to close */}
          <Button className="w-full h-11 bg-red-600 hover:bg-red-700 font-bold uppercase text-sm gap-2" onClick={handleCloseTable} data-testid="close-table-btn">
            <DoorOpen className="w-4 h-4" /> ENCERRAR MESA
          </Button>
        </div>
      )}

      {/* Encerrar mesa (sem banner de pagamento — só quando sessão corrente foi paga) */}
      {!lastPaymentResult && sessionPending <= 0 && total > 0 && sessionPayments.length > 0 && (
        <div className="mb-4">
          <Button className="w-full h-11 bg-red-600 hover:bg-red-700 font-bold uppercase text-sm gap-2" onClick={handleCloseTable} data-testid="close-table-btn-2">
            <DoorOpen className="w-4 h-4" /> ENCERRAR MESA
          </Button>
        </div>
      )}

      {/* Applied discount banner */}
      {appliedDiscount > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z" /></svg>
            <span className="text-sm font-medium text-amber-800">Desconto aplicado: <strong>{formatPrice(appliedDiscount)}</strong></span>
          </div>
          <button onClick={() => setAppliedDiscount(0)} className="text-amber-600 hover:text-amber-800 text-sm font-bold">Remover</button>
        </div>
      )}

      {/* Secondary Action Buttons */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Button variant="outline" className="border-emerald-300 text-emerald-700 font-bold uppercase text-xs gap-1.5" onClick={() => setShowSplit(true)} data-testid="split-bill-btn">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/></svg>
          DIVIDIR CUENTA
        </Button>
        <Button variant="outline" className="border-blue-300 text-blue-700 font-bold uppercase text-xs gap-1.5" onClick={() => { setShowTransferItem(true); setSelectedTransferItems([]); setTargetTableId(""); }} data-testid="transfer-item-btn">
          <ArrowRightLeft className="w-3.5 h-3.5" /> TRANSFERIR ITEM
        </Button>
        <Button variant="outline" className="border-indigo-300 text-indigo-700 font-bold uppercase text-xs gap-1.5" onClick={() => { setShowTransferAll(true); setTargetTableId(""); }} data-testid="transfer-all-btn">
          <ArrowRightLeft className="w-3.5 h-3.5" /> TRANSFERIR MESA
        </Button>
        <Button variant="outline" className="border-red-300 text-red-600 font-bold uppercase text-xs gap-1.5" onClick={() => setShowCancel(true)} data-testid="cancel-table-btn">
          <XCircle className="w-3.5 h-3.5" /> CANCELAR MESA
        </Button>
        <Button variant="outline" className="border-orange-300 text-orange-600 font-bold uppercase text-xs gap-1.5" onClick={forceResetTable} title="Limpar estado preso — não cancela pedidos" data-testid="force-reset-btn">
          <DoorOpen className="w-3.5 h-3.5" /> LIMPAR MESA
        </Button>
        <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 ml-auto" onClick={deleteTable} data-testid="delete-table-btn">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <TableDetailQrSection
        table={table}
        tableName={tableName}
        menuUrl={menuUrl}
        total={total}
        finalTotal={finalTotal}
        appliedDiscount={appliedDiscount}
        sessionPending={sessionPending}
        orders={orders}
        sessionPayments={sessionPayments}
      />

      <TableAccountContent
        loading={loading}
        orders={orders}
        payments={payments}
        account={account}
        total={total}
        finalTotal={finalTotal}
        appliedDiscount={appliedDiscount}
        user={user}
        restaurantData={restaurantData}
        table={table}
        handleEmitInvoice={handleEmitInvoice}
        processingInvoice={processingInvoice}
        authAxios={authAxios}
        onRefresh={fetchAccount}
      />

      <TableDialogs
        showCancel={showCancel} setShowCancel={setShowCancel}
        showSplit={showSplit} setShowSplit={setShowSplit}
        showTransferItem={showTransferItem} setShowTransferItem={setShowTransferItem}
        showTransferAll={showTransferAll} setShowTransferAll={setShowTransferAll}
        showPayment={showPayment} resetPaymentState={resetPaymentState}
        showDiscount={showDiscount} setShowDiscount={setShowDiscount}
        tableName={tableName} otherTables={otherTables} allItems={allItems} total={total}
        handleCancel={handleCancel}
        splitMode={splitMode} setSplitMode={setSplitMode}
        splitPersons={splitPersons} setSplitPersons={setSplitPersons}
        splitResult={splitResult} setSplitResult={setSplitResult}
        selectedSplitItems={selectedSplitItems} toggleSplitItem={toggleSplitItem} handleSplitCalc={handleSplitCalc}
        selectedTransferItems={selectedTransferItems} toggleTransferItem={toggleTransferItem}
        targetTableId={targetTableId} setTargetTableId={setTargetTableId}
        handleTransferItems={handleTransferItems} handleTransferAll={handleTransferAll}
        paymentMode={paymentMode} setPaymentMode={setPaymentMode}
        partialPayments={partialPayments} handleRemovePartialPayment={handleRemovePartialPayment}
        remainingToPay={remainingToPay} finalTotal={finalTotal}
        totalPartialPaid={totalPartialPaid} appliedDiscount={appliedDiscount}
        currentPartialAmount={currentPartialAmount} setCurrentPartialAmount={setCurrentPartialAmount}
        paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
        cardType={cardType} setCardType={setCardType}
        cashReceived={cashReceived} setCashReceived={setCashReceived}
        needsChange={needsChange} setNeedsChange={setNeedsChange}
        changeAmount={changeAmount} partialCashChange={partialCashChange}
        handleAddPartialPayment={handleAddPartialPayment}
        processingPayment={processingPayment} handlePayment={handlePayment}
        discountType={discountType} setDiscountType={setDiscountType}
        discountValue={discountValue} setDiscountValue={setDiscountValue}
        handleApplyDiscount={handleApplyDiscount}
      />
    </div>
  );
}
export { TableDetail };
